import { useState } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectQuotesSortedByRecent, selectQuoteCount } from '../../store/selectors'
import { addQuote, updateQuote, deleteQuote } from '../../store/quotesSlice'
import { Quote } from '../../types/quote'
import QuoteForm from '../../components/Quote/QuoteForm'
import QuoteList from '../../components/Quote/QuoteList'
import styles from './QuotesManager.module.css'

function QuotesManager() {
  const dispatch = useAppDispatch()
  const quotes = useAppSelector(selectQuotesSortedByRecent)
  const quoteCount = useAppSelector(selectQuoteCount)

  const [isAdding, setIsAdding] = useState(false)
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null)

  const handleAdd = (text: string, author?: string) => {
    dispatch(addQuote({ text, author }))
    setIsAdding(false)
  }

  const handleUpdate = (text: string, author?: string) => {
    if (editingQuote) {
      dispatch(updateQuote({ id: editingQuote.id, text, author }))
      setEditingQuote(null)
    }
  }

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      dispatch(deleteQuote(id))
    }
  }

  const handleEdit = (quote: Quote) => {
    setEditingQuote(quote)
    setIsAdding(false)
  }

  const handleCancelEdit = () => {
    setEditingQuote(null)
    setIsAdding(false)
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Manage Quotes</h2>
        <p className={styles.subtitle}>
          {quoteCount} quote{quoteCount !== 1 ? 's' : ''} in your collection
        </p>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingQuote) && (
        <div className={styles.formSection}>
          <h3 className={styles.formTitle}>
            {editingQuote ? 'Edit Quote' : 'Add New Quote'}
          </h3>
          <QuoteForm
            initialText={editingQuote?.text}
            initialAuthor={editingQuote?.author}
            onSubmit={editingQuote ? handleUpdate : handleAdd}
            onCancel={handleCancelEdit}
            submitLabel={editingQuote ? 'Update' : 'Add Quote'}
          />
        </div>
      )}

      {/* Add Button */}
      {!isAdding && !editingQuote && (
        <button
          className={styles.addButton}
          onClick={() => setIsAdding(true)}
        >
          <span className={styles.addIcon}>+</span>
          Add New Quote
        </button>
      )}

      {/* Quotes List */}
      {quoteCount === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📝</div>
          <p className={styles.emptyText}>
            No quotes yet. Add your first motivational quote above.
          </p>
        </div>
      ) : (
        <QuoteList
          quotes={quotes}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}

export default QuotesManager
